import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { literatureReviewPaths } from '@/paths';
import {
  useGetAbstractScreeningByIdService,
  useGetFullTextScreeningService,
  useGetLiteratureReviewService,
} from '@/services/literatureReviews';
import { ArrowLeft, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EditLiteratureReviewModal } from '../../components/EditLiteratureReviewModal';

const steps = [
  {
    key: 'defineSearch',
    label: 'Search',
    stepNumber: 1,
    path: literatureReviewPaths.steps.search,
  },
  {
    key: 'abstractScreening',
    label: 'Screening',
    stepNumber: 2,
    path: literatureReviewPaths.steps.abstractScreening,
  },
  {
    key: 'fullTextScreening',
    label: 'Analysis',
    stepNumber: 3,
    path: literatureReviewPaths.steps.fullTextScreening,
  },
] as const;

export type StepKey = (typeof steps)[number]['key'];

export const LiteratureReviewHeader: FC = () => {
  const navigate = useNavigate();
  const { literatureReview_id: literatureReviewId } = useParams();

  const { literatureReview } =
    useGetLiteratureReviewService(literatureReviewId);
  const { abstractScreening } =
    useGetAbstractScreeningByIdService(literatureReviewId);
  const { fullTextScreening } =
    useGetFullTextScreeningService(literatureReviewId);

  const handleBackClick = () => {
    navigate('../');
  };

  const isStepAvailable: Record<StepKey, boolean> = {
    defineSearch: true,
    abstractScreening: !!abstractScreening?.id,
    fullTextScreening: !!fullTextScreening?.id,
  };

  const getCurrentStep = (): (typeof steps)[number] => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    const step = steps.find(step => step.path === lastSegment) ?? steps[0];
    return step;
  };

  const currentStep = getCurrentStep();

  const getStepStatus = (stepKey: (typeof steps)[number]['key']) => {
    const currentIndex = currentStep.stepNumber;
    const stepIndex = steps.find(step => step.key === stepKey)?.stepNumber;

    if (stepIndex && stepIndex < currentIndex) return 'completed';
    if (stepIndex && stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const nextStep = steps.find(
    step => step.stepNumber === currentStep.stepNumber + 1
  );
  const previousStep = steps.find(
    step => step.stepNumber === currentStep.stepNumber - 1
  );

  const handleNextClick = () => {
    if (nextStep) {
      navigate(nextStep.path);
    }
  };

  const handlePreviousClick = () => {
    if (previousStep) {
      navigate(previousStep.path);
    }
  };

  return (
    <Box
      w="full"
      px="6"
      py="4"
      className="grid grid-cols-3 bg-white border-b border-neutral-200"
      alignItems="center"
      justifyContent="between"
    >
      <FlexRow alignItems="center" gap="2">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="h-6 w-6 shrink-0 rounded"
        >
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <Text variant="body" color="primary">
          {literatureReview?.name}
        </Text>
        <EditLiteratureReviewModal literatureReviewId={literatureReviewId} />
      </FlexRow>

      <FlexRow gap="1.5" alignItems="center" justifyContent="center">
        {steps.map((step, index) => {
          const status = getStepStatus(step.key);
          const isLast = index === steps.length - 1;
          const isHighlighted = status === 'current' || status === 'completed';
          const isCompleted = status === 'completed';

          return (
            <FlexRow key={step.key} alignItems="center" gap="1.5">
              <FlexRow gap="1.5" alignItems="center">
                {isCompleted ? (
                  <FlexRow
                    alignItems="center"
                    justifyContent="center"
                    w="6"
                    h="6"
                    br="full"
                    className={
                      isHighlighted
                        ? 'bg-yellow-400 text-neutral-900'
                        : 'bg-neutral-200 text-neutral-500'
                    }
                  >
                    <Check className="w-4 h-4" />
                  </FlexRow>
                ) : (
                  <FlexRow
                    alignItems="center"
                    justifyContent="center"
                    w="6"
                    h="6"
                    br="full"
                    className={
                      isHighlighted
                        ? 'bg-yellow-400 text-neutral-900'
                        : 'bg-neutral-200 text-neutral-500'
                    }
                  >
                    <Text variant="body-sm" color="primary">
                      {step.stepNumber}
                    </Text>
                  </FlexRow>
                )}
                <Text
                  variant="body-sm"
                  color={isHighlighted ? 'primary' : 'tertiary'}
                >
                  {step.label}
                </Text>
              </FlexRow>
              {!isLast && (
                <div className="h-0.5 w-8 bg-neutral-200 flex-shrink-0" />
              )}
            </FlexRow>
          );
        })}
      </FlexRow>
      <FlexRow alignItems="center" justifyContent="end" gap="2">
        {previousStep && (
          <Button
            variant="outline"
            size="sm"
            disabled={!isStepAvailable[previousStep.key]}
            onClick={handlePreviousClick}
          >
            <ChevronLeft />
            {previousStep.label}
          </Button>
        )}
        {nextStep && (
          <Button
            variant="outline"
            size="sm"
            disabled={!isStepAvailable[nextStep.key]}
            onClick={handleNextClick}
          >
            {nextStep.label}
            <ChevronRight />
          </Button>
        )}
      </FlexRow>
    </Box>
  );
};
